import React, { useState, useEffect, useRef } from 'react';
import AvatarDisplay from './AvatarDisplay';
import { Link } from 'react-router-dom';

const Tweet = ({
  tweet,
  onLike,
  onRepost,
  onDelete,
  onViewComments,  // For viewing comments
  isViewingComments,  // Highlight if viewing comments
  currentUserENS,
  currentUserAddress
}) => {
  const [showActions, setShowActions] = useState(false);
  const [showLightbox, setShowLightbox] = useState(false);
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);
  const [imgError, setImgError] = useState(false);
  const [videoLoading, setVideoLoading] = useState(true);
  const [isMobile, setIsMobile] = useState(false);
  const [shouldAutoplayVideo, setShouldAutoplayVideo] = useState(false);
  
  const videoRef = React.useRef(null);
  const [contextMenu, setContextMenu] = useState(null);
  const longPressTimerRef = useRef(null);

  // Get author address from multiple possible locations
  const authorAddress = tweet.author?.address ||
                       tweet.author_address ||
                       tweet.author_ens_address ||
                       tweet.author?.ensAddress;

  const isOwnPost = currentUserAddress?.toLowerCase() === authorAddress?.toLowerCase();

  // Check for mobile on mount and resize
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Setup video autoplay with intersection observer
  useEffect(() => {
    if (!tweet.url || !isVideo || !videoRef.current) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting && videoRef.current) {
            // Video is in viewport - try to autoplay
            setShouldAutoplayVideo(true);
            setTimeout(() => {
              if (videoRef.current) {
                const playPromise = videoRef.current.play();
                if (playPromise !== undefined) {
                  playPromise.catch(error => {
                    // Autoplay failed, show controls instead
                    console.log('Video autoplay failed:', error);
                    if (videoRef.current) {
                      videoRef.current.controls = true;
                    }
                  });
                }
              }
            }, 300);
          } else if (videoRef.current) {
            // Video is out of viewport - pause it
            videoRef.current.pause();
            setShouldAutoplayVideo(false);
          }
        });
      },
      { threshold: 0.5 }
    );

    observer.observe(videoRef.current);

    return () => {
      if (videoRef.current) {
        observer.unobserve(videoRef.current);
      }
    };
  }, [tweet.url, tweet.id]);

  // ============ NATIVE EVENT LISTENERS FOR RIGHT-CLICK / LONG-PRESS ============
  useEffect(() => {
    const tweetElement = document.querySelector(`[data-post-id="${tweet.id}"]`);
    if (!tweetElement || !isOwnPost || !onDelete) return;

    let touchStartX = 0, touchStartY = 0;

    const handleNativeContextMenu = (e) => {
      e.preventDefault();
      e.stopPropagation();
      console.log('🎯 Native contextmenu fired');
      const x = e.clientX;
      const y = e.clientY;
      setContextMenu({ x, y, postId: tweet.id });
    };

    const handleNativeTouchStart = (e) => {
      const touch = e.touches[0];
      touchStartX = touch.clientX;
      touchStartY = touch.clientY;
      console.log('📱 touchstart at', touchStartX, touchStartY);

      longPressTimerRef.current = setTimeout(() => {
        console.log('⏰ long press timer fired');
        e.preventDefault();
        e.stopPropagation();
        setContextMenu({
          x: touchStartX,
          y: touchStartY,
          postId: tweet.id
        });
        const preventClick = (e) => {
          e.preventDefault();
          e.stopPropagation();
          document.removeEventListener('click', preventClick, true);
        };
        document.addEventListener('click', preventClick, true);
      }, 800); // adjust as needed
    };

    const handleTouchMove = (e) => {
      if (longPressTimerRef.current) {
        const touch = e.touches[0];
        if (touch) {
          const dx = Math.abs(touch.clientX - touchStartX);
          const dy = Math.abs(touch.clientY - touchStartY);
          console.log('📱 touchmove', dx, dy);
          if (dx > 15 || dy > 15) { // threshold
            console.log('📱 movement detected, clearing timer');
            clearTimeout(longPressTimerRef.current);
            longPressTimerRef.current = null;
          }
        }
      }
    };

    const handleTouchEnd = () => {
      console.log('📱 touchend');
      clearTimeout(longPressTimerRef.current);
    };

    tweetElement.addEventListener('contextmenu', handleNativeContextMenu);
    tweetElement.addEventListener('touchstart', handleNativeTouchStart);
    document.addEventListener('touchmove', handleTouchMove);
    document.addEventListener('touchend', handleTouchEnd);
    document.addEventListener('touchcancel', handleTouchEnd);

    return () => {
      tweetElement.removeEventListener('contextmenu', handleNativeContextMenu);
      tweetElement.removeEventListener('touchstart', handleNativeTouchStart);
      document.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('touchend', handleTouchEnd);
      document.removeEventListener('touchcancel', handleTouchEnd);
      clearTimeout(longPressTimerRef.current);
    };
  }, [tweet.id, isOwnPost, onDelete]);

  // Helper function to get authenticated file URL using your zhat_token
  const getAuthenticatedUrl = (url) => {
    if (!url) return url;

    const token = localStorage.getItem('zhat_token');
    if (!token) {
      console.warn('No auth token found for image URL');
      return url;
    }

    const separator = url.includes('?') ? '&' : '?';
    return `${url}${separator}token=${encodeURIComponent(token)}`;
  };

  // Handle like
  const handleLike = (e) => {
    e.stopPropagation();
    if (onLike) {
      onLike(tweet.id);
    }
  };

  // Handle repost
  const handleRepost = (e) => {
    e.stopPropagation();
    if (onRepost && typeof onRepost === 'function') {
      onRepost(tweet.id);
    } else {
      console.warn('Repost function not available');
      alert('Repost functionality is being improved.');
    }
  };

  console.log(`🎨 Rendering Tweet ${tweet.id}: repostCount = ${tweet.repostCount}, repostedByUser = ${tweet.repostedByUser}`);

  // Handle viewing comments
  const handleViewComments = (e) => {
    e.stopPropagation();
    if (onViewComments) {
      onViewComments(tweet.id);
    }
  };

    // ============ CONTEXT MENU (RIGHT CLICK / LONG PRESS) ============

  const handleContextMenu = (e) => {
    // Only show for own posts
    if (!isOwnPost || !onDelete) return;

    e.preventDefault();
    e.stopPropagation();

    // Calculate position relative to viewport
    const x = e.clientX;
    const y = e.clientY;

    setContextMenu({
      x,
      y,
      postId: tweet.id
    });
  };

  const handleTouchStart = (e) => {
    if (!isOwnPost || !onDelete) return;

    const touch = e.touches[0];
    longPressTimerRef.current = setTimeout(() => {
      // Trigger context menu at touch position
      setContextMenu({
        x: touch.clientX,
        y: touch.clientY,
        postId: tweet.id
      });

      // Prevent the click that would follow
      const preventClick = (e) => {
        e.preventDefault();
        e.stopPropagation();
        document.removeEventListener('click', preventClick, true);
      };
      document.addEventListener('click', preventClick, true);
    }, 500); // 500ms long press
  };

  const handleTouchEnd = (e) => {
    clearTimeout(longPressTimerRef.current);
  };

  const handleTouchMove = (e) => {
    clearTimeout(longPressTimerRef.current);
  };

  const handleCloseContextMenu = () => {
    setContextMenu(null);
  };

  const handleDeleteFromMenu = () => {
    if (onDelete) {
      onDelete(tweet.id);
    }
    setContextMenu(null);
  };

  // Format file size
  const formatFileSize = (bytes) => {
    if (!bytes) return '0 B';
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  // Get file icon - Clean text labels
  const getFileIcon = (type) => {
    if (type?.startsWith('image/')) return 'IMG';
    if (type?.startsWith('video/')) return 'VID';
    if (type === 'application/pdf') return 'PDF';
    if (type === 'text/plain') return 'TXT';
    if (type?.includes('document')) return 'DOC';
    return 'FILE';
  };

  // Get media type
  const isImage = tweet.fileType?.startsWith('image/') || tweet.type?.startsWith('image/');
  const isVideo = tweet.fileType?.startsWith('video/') || tweet.type?.startsWith('video/') || tweet.originalName?.match(/\.(mp4|webm|ogg|mov)$/i);
  const isMedia = isImage || isVideo;

  const authenticatedUrl = tweet.url ? getAuthenticatedUrl(tweet.url) : null;

    console.log('🎬 Tweet render check:', {
    id: tweet.id,
    isTemp: tweet.isTemp,
    fileType: tweet.fileType,
    type: tweet.type,
    isImage,
    hasUrl: !!tweet.url,
    filename: tweet.filename,
    originalName: tweet.originalName
  });

  return (
        <div
          className="zpost-tweet"
          data-post-id={tweet.id}
          style={{
            padding: '1rem 1.5rem',
            borderBottom: '1px solid var(--border)',
            background: isViewingComments ? 'rgba(255, 215, 0, 0.05)' : 'var(--black)',
            transition: 'background-color 0.2s ease',
            position: 'relative',
            cursor: 'pointer',
            borderLeft: isViewingComments ? '3px solid var(--gold)' : 'none'
          }}
          onClick={handleViewComments}
          onContextMenu={handleContextMenu}
          onMouseEnter={() => setShowActions(true)}
          onMouseLeave={() => setShowActions(false)}
        >
          <div style={{ display: 'flex', gap: '0.75rem' }}>
            {/* Avatar */}
            <div style={{ flexShrink: 0 }}>
              <AvatarDisplay
                ensName={tweet.author.ensName}
                address={tweet.author.address}
                size={48}
              />
            </div>

            {/* Content */}
            <div style={{ flex: 1, minWidth: 0 }}>
              {/* Header */}
              <div style={{
                display: 'flex',
                alignItems: 'baseline',
                gap: '0.5rem',
                marginBottom: '0.25rem',
                flexWrap: 'wrap'
              }}>
                <Link
                  to={`/profile/${tweet.author.address}`}
                  onClick={(e) => e.stopPropagation()}
                  style={{ textDecoration: 'none', color: 'inherit' }}
                >
                  <div style={{
                    fontWeight: 'bold',
                    color: 'var(--text-light)',
                    fontSize: '0.95rem',
                    cursor: 'pointer'
                  }}>
                    {tweet.author.displayName}
                  </div>
                </Link>
                <Link
                  to={`/profile/${tweet.author.address}`}
                  onClick={(e) => e.stopPropagation()}
                  style={{ textDecoration: 'none', color: 'inherit' }}
                >
                  <div style={{
                    color: 'var(--gold)',
                    fontSize: '0.85rem',
                    wordBreak: 'break-all',
                    cursor: 'pointer'
                  }}>
                    {tweet.author.ensName}
                  </div>
                </Link>
                <div style={{
                  color: 'var(--text-secondary)',
                  fontSize: '0.8rem',
                  marginLeft: 'auto'
                }}>
                  {tweet.displayTime || tweet.timestamp}
                </div>
              </div>

              {/* Content text */}
              <div style={{
                color: 'var(--text-light)',
                fontSize: '0.95rem',
                lineHeight: '1.4',
                marginBottom: '0.75rem',
                wordBreak: 'break-word'
              }}>
                {tweet.content}
              </div>

            {/* File attachment with token authentication */}
            {tweet.url && tweet.originalName && authenticatedUrl && (
              <div style={{ marginBottom: '0.75rem' }}>
                {isImage ? (
                  // IMAGE with token authentication
                  <div>
                    {/* Image preview */}
                    <div
                      style={{
                        marginBottom: '0.75rem',
                        borderRadius: '12px',
                        overflow: 'hidden',
                        border: '1px solid var(--border)',
                        background: 'var(--card-bg)',
                        maxWidth: '100%',
                        position: 'relative',
                        minHeight: '200px'
                      }}
                    >
                      <img
                        src={authenticatedUrl}
                        alt={tweet.originalName}
                        style={{
                          width: '100%',
                          height: 'auto',
                          display: 'block',
                          maxHeight: '500px',
                          objectFit: 'contain',
                          cursor: 'pointer'
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          setShowLightbox(true);
                        }}
                        onError={() => setImgError(true)}
                        onLoad={() => setImgError(false)}
                      />

                      {/* Image overlay - only shown on hover */}
                      <div
                        style={{
                          position: 'absolute',
                          bottom: '0',
                          left: '0',
                          right: '0',
                          background: 'linear-gradient(to top, rgba(0,0,0,0.8), transparent)',
                          padding: '0.75rem',
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          opacity: '0',
                          transition: 'opacity 0.2s ease',
                          pointerEvents: 'none'
                        }}
                        className="image-overlay"
                      >
                        <div style={{
                          color: 'var(--text-light)',
                          fontSize: '0.8rem',
                          fontWeight: 'bold',
                          maxWidth: '70%',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}>
                          {tweet.originalName}
                        </div>
                        <div
                          style={{
                            background: 'var(--gold)',
                            color: 'var(--black)',
                            border: 'none',
                            padding: '0.25rem 0.75rem',
                            borderRadius: '4px',
                            fontSize: '0.8rem',
                            fontWeight: 'bold',
                            pointerEvents: 'auto',
                            cursor: 'pointer'
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            window.open(authenticatedUrl, '_blank');
                          }}
                        >
                          Open
                        </div>
                      </div>
                    </div>

                    {/* Image info bar */}
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      fontSize: '0.75rem',
                      color: 'var(--text-secondary)',
                      padding: '0.5rem 0',
                      borderBottom: '1px solid var(--border)',
                      marginBottom: '0.5rem'
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <span style={{
                          textTransform: 'uppercase',
                          fontWeight: 'bold',
                          color: 'var(--gold)',
                          background: 'rgba(255, 215, 0, 0.1)',
                          padding: '0.2rem 0.5rem',
                          borderRadius: '4px',
                          border: '1px solid var(--border)'
                        }}>
                          {tweet.fileType?.split('/')[1]?.toUpperCase() || 'IMAGE'}
                        </span>
                        <span>•</span>
                        <span>{formatFileSize(tweet.fileSize || tweet.size)}</span>
                        <span>•</span>
                        <span>{tweet.created_at ? new Date(tweet.created_at).toLocaleDateString() : new Date(tweet.timestamp || Date.now()).toLocaleDateString()}</span>
                      </div>
                      <a
                        href={authenticatedUrl}
                        download={tweet.originalName}
                        style={{
                          color: 'var(--gold)',
                          textDecoration: 'none',
                          fontSize: '0.75rem',
                          fontWeight: 'bold',
                          padding: '0.25rem 0.75rem',
                          background: 'rgba(255, 215, 0, 0.1)',
                          borderRadius: '4px',
                          border: '1px solid var(--border)',
                          transition: 'all 0.2s ease'
                        }}
                        onClick={(e) => e.stopPropagation()}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background = 'rgba(255, 215, 0, 0.2)';
                          e.currentTarget.style.borderColor = 'var(--gold)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = 'rgba(255, 215, 0, 0.1)';
                          e.currentTarget.style.borderColor = 'var(--border)';
                        }}
                      >
                        Download
                      </a>
                    </div>
                  </div>
                ) : isVideo ? (
                  // VIDEO with authentication
                  <div>
                    <div style={{
                      marginBottom: '0.75rem',
                      borderRadius: '12px',
                      overflow: 'hidden',
                      border: '1px solid var(--border)',
                      background: 'var(--card-bg)',
                      maxWidth: '100%',
                      position: 'relative',
                      minHeight: '200px'
                    }}>
                      <video
                        ref={videoRef}
                        src={authenticatedUrl}
                        controls
                        autoPlay={shouldAutoplayVideo}
                        muted={shouldAutoplayVideo}
                        loop
                        playsInline
                        style={{
                          width: '100%',
                          height: 'auto',
                          display: 'block',
                          maxHeight: isMobile ? '300px' : '500px',
                          backgroundColor: videoLoading ? 'var(--card-bg)' : 'var(--black)',
                          borderRadius: '8px',
                          border: '1px solid var(--border)',
                          opacity: videoLoading ? 0.5 : 1,
                          transition: 'opacity 0.3s ease'
                        }}
                        controlsList="nodownload"
                        onPlay={() => setIsVideoPlaying(true)}
                        onPause={() => setIsVideoPlaying(false)}
                        onEnded={() => setIsVideoPlaying(false)}
                        onLoadedData={() => setVideoLoading(false)}
                        onError={() => setVideoLoading(false)}
                        preload="metadata"
                        onClick={(e) => e.stopPropagation()}
                      />

                      {/* Video loading indicator */}
                      {videoLoading && (
                        <div style={{
                          position: 'absolute',
                          top: '50%',
                          left: '50%',
                          transform: 'translate(-50%, -50%)',
                          color: 'var(--gold)',
                          fontSize: '0.8rem',
                          fontWeight: 'bold',
                          background: 'rgba(0,0,0,0.7)',
                          padding: '0.5rem 1rem',
                          borderRadius: '4px'
                        }}>
                          Loading video...
                        </div>
                      )}

                      {/* Autoplay indicator */}
                      {shouldAutoplayVideo && (
                        <div style={{
                          position: 'absolute',
                          top: '0.5rem',
                          right: '0.5rem',
                          background: 'rgba(0,0,0,0.7)',
                          color: 'var(--gold)',
                          fontSize: '0.7rem',
                          padding: '0.2rem 0.5rem',
                          borderRadius: '4px',
                          border: '1px solid var(--gold)'
                        }}>
                          Auto-playing
                        </div>
                      )}

                      {/* Video overlay */}
                      <div style={{
                        position: 'absolute',
                        bottom: '0',
                        left: '0',
                        right: '0',
                        background: 'linear-gradient(to top, rgba(0,0,0,0.9), transparent)',
                        padding: '0.75rem',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        borderBottomLeftRadius: '12px',
                        borderBottomRightRadius: '12px'
                      }}>
                        <div style={{
                          color: 'var(--gold)',
                          fontSize: '0.8rem',
                          fontWeight: 'bold',
                          maxWidth: '70%',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          textShadow: '0 1px 2px rgba(0,0,0,0.8)'
                        }}>
                          {tweet.originalName}
                        </div>
                      </div>
                    </div>

                    {/* Video info */}
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      fontSize: '0.75rem',
                      color: 'var(--text-secondary)',
                      padding: '0.5rem 0',
                      borderBottom: '1px solid var(--border)',
                      marginBottom: '0.5rem'
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <span style={{
                          textTransform: 'uppercase',
                          fontWeight: 'bold',
                          color: 'var(--gold)',
                          background: 'rgba(255, 215, 0, 0.1)',
                          padding: '0.2rem 0.5rem',
                          borderRadius: '4px',
                          border: '1px solid var(--border)'
                        }}>
                          {tweet.fileType?.split('/')[1]?.toUpperCase() || 'VIDEO'}
                        </span>
                        <span>•</span>
                        <span>{formatFileSize(tweet.fileSize || tweet.size)}</span>
                        <span>•</span>
                        <span>{tweet.created_at ? new Date(tweet.created_at).toLocaleDateString() : new Date(tweet.timestamp || Date.now()).toLocaleDateString()}</span>
                      </div>
                      <a
                        href={authenticatedUrl}
                        download={tweet.originalName}
                        style={{
                          color: 'var(--gold)',
                          textDecoration: 'none',
                          fontSize: '0.75rem',
                          fontWeight: 'bold',
                          padding: '0.25rem 0.75rem',
                          background: 'rgba(255, 215, 0, 0.1)',
                          borderRadius: '4px',
                          border: '1px solid var(--border)',
                          transition: 'all 0.2s ease'
                        }}
                        onClick={(e) => e.stopPropagation()}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background = 'rgba(255, 215, 0, 0.2)';
                          e.currentTarget.style.borderColor = 'var(--gold)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = 'rgba(255, 215, 0, 0.1)';
                          e.currentTarget.style.borderColor = 'var(--border)';
                        }}
                      >
                        Download
                      </a>
                    </div>
                  </div>
                ) : (
                  // NON-MEDIA FILES
                  <div style={{
                    padding: '0.75rem',
                    background: 'rgba(255, 215, 0, 0.05)',
                    border: '1px solid var(--border)',
                    borderRadius: '8px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.75rem'
                  }}>
                    <div style={{
                      fontSize: '1.25rem',
                      color: 'var(--gold)',
                      minWidth: '24px',
                      textAlign: 'center',
                      fontWeight: 'bold'
                    }}>
                      {getFileIcon(tweet.fileType || tweet.type)}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{
                        fontSize: '0.9rem',
                        color: 'var(--text-light)',
                        fontWeight: 'bold',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis'
                      }}>
                        {tweet.originalName}
                      </div>
                      <div style={{
                        fontSize: '0.75rem',
                        color: 'var(--text-secondary)',
                        display: 'flex',
                        gap: '0.5rem',
                        alignItems: 'center'
                      }}>
                        <span>
                          {tweet.fileType?.split('/')[1]?.toUpperCase() || 'FILE'}
                        </span>
                        <span>•</span>
                        <span>{formatFileSize(tweet.fileSize || tweet.size)}</span>
                      </div>
                    </div>
                    <a
                      href={authenticatedUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        background: 'var(--gold)',
                        color: 'var(--black)',
                        border: 'none',
                        padding: '0.35rem 0.75rem',
                        borderRadius: '4px',
                        fontSize: '0.8rem',
                        fontWeight: 'bold',
                        textDecoration: 'none',
                        cursor: 'pointer',
                        minWidth: '70px',
                        textAlign: 'center'
                      }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      {tweet.fileType === 'application/pdf' ? 'View' : 'Save'}
                    </a>
                  </div>
                )}
              </div>
            )}

            {/* Actions */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '2rem',
              marginTop: '0.5rem',
              paddingTop: '0.5rem',
              borderTop: '1px solid rgba(255, 255, 255, 0.05)'
            }}>
              {/* Like button with Font Awesome */}
              <button
                onClick={handleLike}
                disabled={tweet.likedByUser}
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: tweet.likedByUser ? 'var(--gold)' : 'var(--text-secondary)',
                  cursor: tweet.likedByUser ? 'default' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.25rem',
                  fontSize: '0.85rem',
                  padding: '0.25rem 0.5rem',
                  borderRadius: '4px',
                  transition: 'all 0.2s ease',
                  minWidth: '44px',
                  minHeight: '32px',
                  opacity: tweet.likedByUser ? 0.7 : 1
                }}
                onMouseEnter={(e) => {
                  if (!tweet.likedByUser) {
                    e.currentTarget.style.color = 'var(--gold)';
                    e.currentTarget.style.background = 'rgba(255, 215, 0, 0.1)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!tweet.likedByUser) {
                    e.currentTarget.style.color = 'var(--text-secondary)';
                    e.currentTarget.style.background = 'transparent';
                  }
                }}
                title={tweet.likedByUser ? 'Already liked' : 'Like'}
              >
                <span style={{
                  fontSize: '1rem',
                  display: 'flex',
                  alignItems: 'center'
                }}>
                  {tweet.likedByUser ? (
                    <i className="fas fa-heart" style={{ color: 'var(--gold)' }}></i>
                  ) : (
                    <i className="far fa-heart"></i>
                  )}
                </span>
                <span style={{
                  color: 'inherit',
                  fontWeight: 'bold'
                }}>
                  {tweet.likeCount || 0}
                </span>
              </button>

              {/* Repost button */}
              <button
                onClick={handleRepost}
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: tweet.repostedByUser ? 'var(--gold)' : 'var(--text-secondary)',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.25rem',
                  fontSize: '0.85rem',
                  padding: '0.25rem 0.5rem',
                  borderRadius: '4px',
                  transition: 'all 0.2s ease',
                  minWidth: '44px',
                  minHeight: '32px'
                }}
                title={tweet.repostedByUser ? 'Undo repost' : 'Repost'}
              >
                <span style={{
                  fontSize: '1rem',
                  display: 'flex',
                  alignItems: 'center'
                }}>
                  <i
                    className="fas fa-retweet"
                    style={{
                      color: tweet.repostedByUser ? 'var(--gold)' : 'var(--text-secondary)',
                      opacity: tweet.repostedByUser ? 1 : 0.7
                    }}
                  ></i>
                </span>
                <span style={{
                  color: 'inherit',
                  fontWeight: 'bold'
                }}>
                  {tweet.repostCount || 0}
                </span>
              </button>   {/* closes repost button */}

              {/* Comment button - Gold when has comments */}
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  if (onViewComments) {
                    onViewComments(tweet.id);
                  }
                }}
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: tweet.comment_count > 0 ? 'var(--gold)' : 'var(--text-secondary)',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.25rem',
                  fontSize: '0.85rem',
                  padding: '0.25rem 0.5rem',
                  borderRadius: '4px',
                  transition: 'all 0.2s ease',
                  minWidth: '44px',
                  minHeight: '32px',
                  fontWeight: tweet.comment_count > 0 ? 'bold' : 'normal'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.color = 'var(--gold)';
                  e.currentTarget.style.background = 'rgba(255, 215, 0, 0.1)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.color = tweet.comment_count > 0 ? 'var(--gold)' : 'var(--text-secondary)';
                  e.currentTarget.style.background = 'transparent';
                }}
                title={tweet.comment_count > 0 ? `View ${tweet.comment_count} comment${tweet.comment_count === 1 ? '' : 's'}` : 'No comments yet'}
              >
                <span style={{
                  fontSize: '0.9rem',
                  color: 'inherit'
                }}>
                  💬
                </span>
                <span style={{
                  color: 'inherit',
                  fontWeight: 'inherit'
                }}>
                  {tweet.comment_count || 0}
                </span>
              </button>

              {/* Share button */}
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  const tweetUrl = `${window.location.origin}/post/${tweet.id}`;
                  navigator.clipboard.writeText(tweetUrl);
                  alert('Link copied to clipboard!');
                }}
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: 'var(--text-secondary)',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.25rem',
                  fontSize: '0.85rem',
                  padding: '0.25rem 0.5rem',
                  borderRadius: '4px',
                  transition: 'all 0.2s ease',
                  minWidth: '44px',
                  minHeight: '32px'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.color = 'var(--gold)';
                  e.currentTarget.style.background = 'rgba(255, 215, 0, 0.1)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.color = 'var(--text-secondary)';
                  e.currentTarget.style.background = 'transparent';
                }}
                title="Copy link"
              >
                <span style={{
                  fontSize: '0.9rem',
                  color: 'inherit',
                  fontWeight: 'bold'
                }}>
                  ↗
                </span>
              </button>
            </div>   {/* closes actions div */}

          </div>   {/* closes content div (flex:1) */}

        </div>   {/* ← NEW: closes the outer flex container */}

        {/* Lightbox overlay for images */}
        {showLightbox && isImage && authenticatedUrl && (
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(0,0,0,0.95)',
              zIndex: 9999,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '1rem'
            }}
            onClick={() => setShowLightbox(false)}
          >
            <div style={{
              position: 'relative',
              maxWidth: '90vw',
              maxHeight: '90vh',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <img
                src={authenticatedUrl}
                alt={tweet.originalName}
                style={{
                  maxWidth: '100%',
                  maxHeight: '90vh',
                  objectFit: 'contain'
                }}
                onClick={(e) => e.stopPropagation()}
              />
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setShowLightbox(false);
                }}
                style={{
                  position: 'absolute',
                  top: '-0.5rem',
                  right: '-0.5rem',
                  background: 'var(--gold)',
                  color: 'var(--black)',
                  border: 'none',
                  width: '32px',
                  height: '32px',
                  borderRadius: '50%',
                  fontSize: '1.2rem',
                  cursor: 'pointer',
                  fontWeight: 'bold',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                ✕
              </button>
            </div>
          </div>
        )}

        {/* Repost indicator */}
        {tweet.isRepost && (
          <div style={{
            fontSize: '0.8rem',
            color: 'var(--text-secondary)',
            marginBottom: '0.25rem',
            display: 'flex',
            alignItems: 'center',
            gap: '0.25rem'
          }}>
            <i className="fas fa-retweet" style={{ color: 'var(--gold)' }}></i>
            <span>Reposted by {tweet.author.displayName}</span>
          </div>
        )}

        {/* Custom context menu for delete */}
        {contextMenu && contextMenu.postId === tweet.id && (
          <>
            <div
              style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                zIndex: 9998
              }}
              onClick={handleCloseContextMenu}
              onContextMenu={(e) => { e.preventDefault(); e.stopPropagation(); handleCloseContextMenu(); }}
            />
            <div
              style={{
                position: 'fixed',
                top: contextMenu.y,
                left: contextMenu.x,
                transform: 'translate(-50%, -50%)',
                background: 'var(--black)',
                border: '1px solid var(--border)',
                borderRadius: '8px',
                padding: '0.5rem 0',
                minWidth: '180px',
                boxShadow: '0 4px 20px rgba(0,0,0,0.5)',
                zIndex: 9999,
                animation: 'fadeIn 0.2s ease'
              }}
            >
              <div
                onClick={handleDeleteFromMenu}
                style={{
                  padding: '0.75rem 1.5rem',
                  color: '#ff6b6b',
                  fontSize: '0.9rem',
                  cursor: 'pointer',
                  transition: 'background 0.2s ease',
                  borderBottom: '1px solid var(--border)',
                  fontWeight: 'bold',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.75rem'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'rgba(128, 0, 0, 0.2)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'transparent';
                }}
              >
                <span style={{ fontSize: '1.1rem' }}>🗑️</span>
                <span>Delete post</span>
              </div>
              <div
                onClick={handleCloseContextMenu}
                style={{
                  padding: '0.75rem 1.5rem',
                  color: 'var(--text-secondary)',
                  fontSize: '0.9rem',
                  cursor: 'pointer',
                  transition: 'background 0.2s ease',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.75rem'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'rgba(255, 255, 255, 0.05)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'transparent';
                }}
              >
                <span style={{ fontSize: '1.1rem' }}>✕</span>
                <span>Cancel</span>
              </div>
            </div>
          </>
        )}

        {/* Temporary post indicator */}
        {tweet.isTemp && (
          <div style={{
            position: 'absolute',
            top: '0.5rem',
            left: '0.5rem',
            background: 'rgba(255, 215, 0, 0.2)',
            color: 'var(--gold)',
            fontSize: '0.7rem',
            padding: '0.1rem 0.3rem',
            borderRadius: '4px',
            border: '1px solid var(--gold)'
          }}>
            Sending...
            </div>
          )}
        </div>
      );
    };

  export default Tweet;
